import { HistoryWorkQuery } from '@Entities/search'
import { PaginationBody, PaginationInfo } from '@Entities/pagination'
import { action, makeObservable, observable, runInAction } from 'mobx'
import { findPageOnLimitChange } from '@App/helpers/pagination/find-page-on-limit-change'
import RootStore from '@App/stores/root-store'
import { searchWorksHistoryRH } from '@App/api/routes/search'
import { calcNumPages } from '@App/helpers/pagination/calc-num-pages'
import { RegistrationStatus, RegistrationStatusAndFilters } from '@App/enum/registration-status'
import { RegistrationHistoryFilter } from '@App/forms/filters/reg-history-filter'
import { Works } from '@Entities/work/works'

export const defaultBebopPagination = {
    page: 1,
    limit: 10,
}

/** Everything besides type/pagination that determines what `_works` holds — see `lastResultsScope`. */
export interface RegistrationHistoryResultsScope {
    rightsholderId: number
    query: HistoryWorkQuery
    filters: RegistrationHistoryFilter | undefined
}

export default class RegistrationHistoryTabStore {
    private rootStore: RootStore

    private _works: Works | undefined
    private _pagination: PaginationBody
    private _paginationInfo: PaginationInfo | undefined
    private _lastResultsScope: RegistrationHistoryResultsScope | undefined

    constructor(rootStore: RootStore) {
        makeObservable<RegistrationHistoryTabStore, '_works' | '_pagination' | '_paginationInfo' | '_lastResultsScope'>(
            this,
            {
                _works: observable,
                _pagination: observable,
                _paginationInfo: observable,
                _lastResultsScope: observable,
                setCurrentPage: action,
                setLimit: action,
                setPagination: action,
                resetStore: action,
                applySearchResults: action,
            }
        )

        this.rootStore = rootStore
        this._pagination = defaultBebopPagination
    }

    /**
     * `_works` is only safe to reuse as seed data for a fresh render when it was fetched under this
     * exact rightsholder/criteria/filter combination — `registrationHistoryStore`'s criteria and
     * filters are shared across all four tabs, and `rightsholderId` can change without this
     * session-persistent store being reset, so neither is implied by this tab's own pagination.
     */
    get lastResultsScope(): RegistrationHistoryResultsScope | undefined {
        return this._lastResultsScope
    }

    get works(): Works | undefined {
        return this._works
    }

    get pagination(): PaginationBody {
        return this._pagination
    }

    get paginationInfo(): PaginationInfo | undefined {
        return this._paginationInfo
    }

    get limit(): number {
        return this._pagination.limit ?? 10
    }

    setCurrentPage(page: number) {
        this._pagination.page = page
    }

    setLimit(num: number): void {
        if (this._works) {
            const newPage = findPageOnLimitChange(this._pagination, this._works.count, num, 1)
            this.setCurrentPage(newPage)
        }
        this._pagination.limit = num
    }

    /** Sets page/limit directly, without the "keep the same item visible" adjustment `setLimit` makes. */
    setPagination(pagination: PaginationBody): void {
        if (pagination.page !== undefined) {
            this._pagination.page = pagination.page
        }
        if (pagination.limit !== undefined) {
            this._pagination.limit = pagination.limit
        }
    }

    resetStore(): void {
        this._works = undefined
        this._pagination = defaultBebopPagination
        this._paginationInfo = undefined
        this._lastResultsScope = undefined
    }

    public reportSearchError(error: unknown): void {
        this.rootStore.popupStore.setAlertFromError(error)
    }

    /**
     * Performs the search request without touching any observable state, so it can be owned by an
     * external cache (TanStack Query).
     */
    public async fetchRegistrationHistory(
        registrationStatusAndFilters: RegistrationStatusAndFilters,
        pagination: PaginationBody,
        filters?: RegistrationHistoryFilter
    ): Promise<Works> {
        const rightsholderId = this.rootStore.rightsholderStore.currentRightsholderId
        let sortByString: string | undefined

        if (registrationStatusAndFilters.status === RegistrationStatus.processing) {
            sortByString = filters?.sort_by ? (filters.sort_by as string) : 'submission_date'
        }

        const defaultFilters =
            registrationStatusAndFilters.defaultFilters?.length > 0
                ? { workHistoryStatusFilter: registrationStatusAndFilters.defaultFilters }
                : {}
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { sort_by: _unused, ...filtersWithoutSort } = filters ?? {}
        const appliedFilters = {
            ...filtersWithoutSort,
            workHistoryStatusFilter:
                filtersWithoutSort?.workHistoryStatusFilter && filtersWithoutSort.workHistoryStatusFilter.length > 0
                    ? filtersWithoutSort.workHistoryStatusFilter
                    : defaultFilters.workHistoryStatusFilter,
        }
        const axiosResponse = await searchWorksHistoryRH(
            rightsholderId,
            pagination,
            registrationStatusAndFilters.status,
            this.rootStore.registrationHistoryStore.currentQuery,
            appliedFilters,
            this.rootStore.registrationHistoryStore.isCriteriaPresent() ? undefined : sortByString
        )
        return { works: axiosResponse.data.data, count: axiosResponse.data.count }
    }

    /** Stores results fetched elsewhere so components still reading the store stay in sync. */
    public applySearchResults(
        response: Works,
        pagination: PaginationBody,
        resultsScope: RegistrationHistoryResultsScope
    ): void {
        runInAction(() => {
            this._works = response
            this._lastResultsScope = resultsScope
        })
        if (pagination.limit && pagination.page) {
            this.setPagination(pagination)
            runInAction(() => {
                this._paginationInfo = calcNumPages(response.count, pagination.limit, pagination.page)
            })
        }
        this.rootStore.workStore.destroyViewWork()
    }

    async searchRegistrationHistory(
        registrationStatusAndFilters: RegistrationStatusAndFilters,
        pagination: PaginationBody = { page: 1, limit: 10 },
        filters?: RegistrationHistoryFilter
    ): Promise<void> {
        try {
            const response = await this.fetchRegistrationHistory(registrationStatusAndFilters, pagination, filters)
            this.applySearchResults(response, pagination, {
                rightsholderId: this.rootStore.rightsholderStore.currentRightsholderId,
                query: this.rootStore.registrationHistoryStore.currentQuery,
                filters,
            })
        } catch (e) {
            this.reportSearchError(e)
        }
    }
}
